// src/pages/ChatBot/configs/RebuildBanner.tsx
//
// Live status banner for an in-flight (or just-finished) document re-process,
// shown at the top of the Configs tab. Split out of ConfigsPanel, which owns the
// polling; this component just renders whatever rebuild slice is current.
// Completed is dismissible (the panel remembers the dismissed version); failed
// surfaces the error and nudges a re-save to retry. Uses the cfg-* classes from
// ConfigsPanel.css, which the panel that mounts it imports.

import type { RebuildState } from './types';

export function RebuildBanner({
  rebuild,
  onDismiss,
}: {
  rebuild: RebuildState;
  onDismiss: () => void;
}) {
  if (rebuild.status === 'queued' || rebuild.status === 'running') {
    return (
      <div className="cfg-rebuild-banner">
        <span className="cfg-spinner" />
        <span className="cfg-rebuild-text">
          Re-processing documents under the new settings…
          {rebuild.progress ? ` (${rebuild.progress.done}/${rebuild.progress.total})` : ''}
        </span>
      </div>
    );
  }

  if (rebuild.status === 'completed') {
    return (
      <div className="cfg-rebuild-banner cfg-rebuild-banner-success">
        <span className="cfg-rebuild-text">
          Document re-processing complete (v{rebuild.toVersion}). Answers now use the
          new settings.
        </span>
        <button
          type="button"
          className="cfg-rebuild-dismiss"
          aria-label="Dismiss"
          onClick={onDismiss}
        >
          ✕
        </button>
      </div>
    );
  }

  // status === 'failed'
  return (
    <div className="cfg-rebuild-banner cfg-rebuild-banner-failed">
      <span className="cfg-rebuild-text">
        Document re-processing failed{rebuild.error ? `: ${rebuild.error}` : ''}. Re-save
        your changes to retry.
      </span>
    </div>
  );
}
